import { forwardRef, useImperativeHandle } from 'react';
import './style.scss';
import type IValidatable from '../validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import { SanitizerService } from '../../../services/sanitizer.service';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';

export interface IConsentState {
  value: boolean;
  validationInfo: ValidationInfo;
}

export interface IConsentProps {
  id: string;
  consentText: string;
  integrityPolicyInfo: string;
  termsAndConditions: string;
  className?: string;
  state: IConsentState;
  onChange?: (value: boolean) => void;
}

const serverValidationName = 'isConsentGiven';

 
export default forwardRef<IValidatable, IConsentProps>(function Consent({ id, consentText, integrityPolicyInfo, termsAndConditions, className, state, onChange = undefined }, ref) {
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${termsAndConditions} - `);
      ValidationEngine.for(updatedValidationInfo).required(state.value).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${termsAndConditions} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  return (
    <div className={`consent ${className}`}>
      <div className="mb-3">
        <fieldset>
          <legend className="h4 mb-3" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(termsAndConditions) }}></legend>
          <div className="form-check d-block">
            <input
              type="checkbox"
              className={`form-check-input ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
              id={id}
              checked={state.value}
              onChange={(e) => {
                onChange?.(e.target.checked);
              }}
              aria-invalid={state.validationInfo.isInvalid}
              aria-describedby={state.validationInfo.isInvalid ? `${id}-errors` : undefined}
              required
            />
            <label className="form-check-label" htmlFor={id}>
              {consentText}
            </label>
            <div id={`${id}-errors`} className="invalid-feedback">
              {state.validationInfo.getErrors().map((error) => (
                <div key={error}>{error}</div>
              ))}
            </div>
          </div>
        </fieldset>
      </div>
      <div className="consent__integrity-policy-info" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(integrityPolicyInfo) }}></div>
    </div>
  );
});
